import {
  PROB_COLS,                 // still imported; not used anymore
  rollingBands,
  priceAtOrAfter as priceAtOrAfterEngine,
  priceAtOrBefore as priceAtOrBeforeEngine,
} from "./engine";
import { INDICATOR_OPTIONS } from "./indicatorOptions";
import { formatZScoreYears, normalizeZScoreIndicatorKey } from "./zScoreConfig";
import { isClassicNovelCombo } from "./comboConfig";

export function computeIndicatorCalculations({
  indicator,
  zScoreYears = 2,
  zScoreSeries,
  pricePoints,
  incomePts,
  rsiSeries,
  fSeries,
  evYieldSeries,
  divYieldZ2ySeries,
  divYieldZ5ySeries,
  pbSeries,
  pegSeries,
  syoSeries,
  srmSeries,
  stdWindow,
  maxSigma,
  vmoSeries,
  rmoSeries,
  tamoSeries,
  nimoSeries,
  cfaoSeries,
  cfaoDeltaSeries,
  mrvzSeries,
  vmzSeries,
  cceSeries,
  oeySeries,
  revSurpriseSeries,
  vddmSeries,
  nuplSeries,
  realizedPriceSeries,
  edSeries,
  riskSeries,
  peDevSeries,
  pbNormalizedSeries,
  pbNormalized5ySeries,
  peNormalizedSeries,
  peNormalized5ySeries,
  poiNormalizedSeries,
  poiNormalized5ySeries,
  pegQuantSeries,
  psNormalizedSeries,
  psNormalized5ySeries,
  cfPriceNormalizedSeries,
  cfPriceNormalized5ySeries,
  pePERatioSeries,
  revValSeries,
  epsValSeries,
  fgpzSeries,
  gagSeries,
}) {
  if (!pricePoints?.length) return null;

  const indicatorKey = normalizeZScoreIndicatorKey(indicator);
  const zYearsLabel = formatZScoreYears(zScoreYears);

  // ===== tiny stats =====
  const mean = a => a.length ? a.reduce((s,x)=>s+x,0)/a.length : 0;
  const std  = a => a.length<2 ? 0 : Math.sqrt(a.reduce((s,x)=>s+(x-mean(a))**2,0)/(a.length-1));
  const pct  = (n,d) => d ? (100*n/d) : null;
  // Geometric mean for returns (compound return): converts % to multipliers, multiplies, takes nth root, converts back
  const geometricMean = (returns) => {
    if (!returns.length) return null;
    const valid = returns.filter(r => Number.isFinite(r) && r > -100); // Filter out invalid or >-100% returns
    if (!valid.length) return null;
    const product = valid.reduce((prod, r) => {
      const multiplier = 1 + (r / 100); // Convert % to multiplier
      return prod * multiplier;
    }, 1);
    const geoMean = (Math.pow(product, 1 / valid.length) - 1) * 100; // nth root, convert back to %
    return Number.isFinite(geoMean) ? geoMean : null;
  };

  // ===== price helpers =====
  const pxAfter  = (t) => (priceAtOrAfterEngine(t, pricePoints)?.value ?? null);
  const pxBefore = (t) => (priceAtOrBeforeEngine(t, pricePoints)?.value ?? null);
  const daysBetween = (t0, t1) => Math.max(0, Math.round((t1 - t0) / (24 * 3600)));
  function forwardReturnBetweenTimes(t0, t1) {
    const p0 = pxAfter(t0);
    const p1 = pxAfter(t1);
    if (!(p0 > 0 && p1 > 0)) return null;
    return (p1 / p0 - 1) * 100;
  }
  function avgPriceBetween(t0, t1) {
    let sum=0, n=0;
    for (const p of pricePoints) {
      if (p.time < t0) continue;
      if (p.time > t1) break;
      if (Number.isFinite(p.value)) { sum+=p.value; n++; }
    }
    if (n) return sum/n;
    const p0 = pxAfter(t0); return Number.isFinite(p0) ? p0 : null;
  }

  // ===== unified columns =====
  const DIST_COLS = ["<-15%","-15–-5%","-5–0%","0–5%","5–15%",">15%"];
  const BASE_SCEN_COLS = ["Scenario","n","Avg %","Geo Avg %","Std %","Avg Days", "Win-to-Loss Ratio", "Sharpe Ratio", ...DIST_COLS];
  const BASE_BUCKET_COLS = ["Bucket","n","Avg %","Geo Avg %","Std %","Avg Days", "Win-to-Loss Ratio", "Sharpe Ratio", ...DIST_COLS];

  // ===== unified summarizers =====
  function summarizeRets({ labelKey, label, rets, durs }) {
    const R = rets.filter(Number.isFinite);
    const n = R.length;
    if (!n) {
      const row = { [labelKey]: label, n: 0, "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—" };
      for (const c of DIST_COLS) row[c] = "—";
      return row;
    }
    const m = mean(R), s = std(R);
    const geoM = geometricMean(R);
    const avgDays = (durs && durs.length) ? Math.round(mean(durs.filter(Number.isFinite))) : null;
    const wins = R.filter(x => x > 0).length;
    const losses = R.filter(x => x < 0).length;
    const winLossRatio = losses > 0 ? (wins / losses) : (wins > 0 ? null : 0); // null => show "∞" when no losses
    // Sharpe = (mean - riskFree) / std; risk-free = 0 (per-trade excess return per unit volatility)
    const sharpe = s > 0 ? (m / s) : null;

    const l15   = pct(R.filter(x => x <= -15).length, n);
    const l5_15 = pct(R.filter(x => x <=  -5 && x > -15).length, n);
    const l0_5  = pct(R.filter(x => x <    0 && x >  -5).length, n);
    const g0_5  = pct(R.filter(x => x >=   0 && x <   5).length, n);
    const g5_15 = pct(R.filter(x => x >=   5 && x <  15).length, n);
    const g15p  = pct(R.filter(x => x >=  15).length, n);

    const row = {
      [labelKey]: label,
      n,
      "Avg %": m.toFixed(2),
      "Geo Avg %": geoM != null ? geoM.toFixed(2) : "—",
      "Std %": s.toFixed(2),
      "Avg Days": avgDays == null ? "—" : String(avgDays),
      "Win-to-Loss Ratio": winLossRatio === null ? "∞" : winLossRatio.toFixed(2),
      "Sharpe Ratio": sharpe != null ? sharpe.toFixed(2) : "—",
      "<-15%":  `${l15.toFixed(2)}%`,
      "-15–-5%":`${l5_15.toFixed(2)}%`,
      "-5–0%":  `${l0_5.toFixed(2)}%`,
      "0–5%":   `${g0_5.toFixed(2)}%`,
      "5–15%":  `${g5_15.toFixed(2)}%`,
      ">15%":   `${g15p.toFixed(2)}%`,
    };
    return row;
  }

  function summarizeBuckets(name, arr, labelKey="Bucket") {
    const vals = arr.map(x=>x.ret).filter(Number.isFinite);
    return summarizeRets({ labelKey, label:name, rets: vals, durs: [] });
  }

  // ================================
  // F-Score → forward to next quarter
  // ================================
  if (indicatorKey === "f_score") {
    const byF = {};
    fSeries?.forEach(pt => {
      const nextQ = incomePts.find(q => q.time > pt.time);
      if (!nextQ) return;
      const ret = forwardReturnBetweenTimes(pt.time, nextQ.time);
      const f = Math.round(pt.value);
      (byF[f] = byF[f] || []).push({ ret });
    });
    const rows = Object.keys(byF)
      .sort((a,b)=>a-b)
      .map(k => summarizeBuckets(`F=${k}`, byF[k]));
    return {
      title: "Indicator Backtesting Statistics — Forward % return until next quarter by F-Score",
      columns: BASE_BUCKET_COLS,
      rows,
    };
  }

  // ================================
  // PEG DCA percentile strategies
  // ================================
  if (indicatorKey === "peg_ratio") {
    const s = Array.isArray(pegQuantSeries)
      ? pegQuantSeries.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — PEG percentile DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    function simulate(entryPct, exitPct) {
      const trades = [], durs = [];
      let i = 0;
      while (i < s.length) {
        if (!(s[i].value < entryPct)) { i++; continue; }
        const start = i;
        let j = i;
        while (j < s.length && s[j].value < entryPct) j++;
        const t0 = s[start].time, t1 = s[j-1].time;
        const avgEntry = avgPriceBetween(t0, t1);
        if (!(avgEntry > 0)) { i = j; continue; }
        let k = j, exitT = null;
        while (k < s.length) { if (s[k].value >= exitPct) { exitT = s[k].time; break; } k++; }
        if (exitT != null) {
          const pExit = pxAfter(exitT);
          if (Number.isFinite(pExit) && pExit > 0) {
            trades.push((pExit/avgEntry - 1) * 100);
            durs.push(daysBetween(t0, exitT));
          }
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const Scenarios = [
      { name:"DCA PEG%<40 → Exit ≥50", entry:40, exit:50 },
      { name:"DCA PEG%<40 → Exit ≥60", entry:40, exit:60 },
      { name:"DCA PEG%<40 → Exit ≥70", entry:40, exit:70 },
      { name:"DCA PEG%<30 → Exit ≥50", entry:30, exit:50 },
      { name:"DCA PEG%<30 → Exit ≥60", entry:30, exit:60 },
      { name:"DCA PEG%<30 → Exit ≥70", entry:30, exit:70 },
    ];
    const rows = Scenarios.map(scn => {
      const { trades, durs } = simulate(scn.entry, scn.exit);
      return summarizeRets({ labelKey:"Scenario", label: scn.name, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — PEG percentile DCA strategies", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // P/B Z-score DCA strategies
  // ================================
  if (indicatorKey === "pb_ratio") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : pbNormalizedSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    const sRaw = Array.isArray(pbSeries) ? pbSeries : [];

    if (s.length && pricePoints.length) {
      function simulate(entryZ, exitZ) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value < entryZ)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryZ) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value >= exitZ) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "DCA z<-1, Exit z≥1", entry: -1, exit: 1 },
        { name: "DCA z<-0.5, Exit z≥1", entry: -0.5, exit: 1 },
        { name: "DCA z<-1, Exit z≥1.5", entry: -1, exit: 1.5 },
        { name: "DCA z<-1.5, Exit z≥1", entry: -1.5, exit: 1 },
      ];
      const rows = Scenarios.map(scn => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return { title: `Indicator Backtesting Statistics — P/B Z-score DCA (${zYearsLabel} avg; enter z<-1; exit z≥1)`, columns: BASE_SCEN_COLS, rows };
    }

    // fallback: simple next-quarter buckets (>1 vs <1)
    const hi = [], lo = [];
    sRaw?.forEach(pt => {
      const nextQ = incomePts.find(q => q.time > pt.time);
      if (!nextQ) return;
      const ret = forwardReturnBetweenTimes(pt.time, nextQ.time);
      (pt.value > 1 ? hi : lo).push({ ret });
    });
    const rows = [ summarizeBuckets("P/B > 1", hi), summarizeBuckets("P/B < 1", lo) ];
    return { title: "Indicator Backtesting Statistics — Forward % return by P/B bucket (until next quarter)", columns: BASE_BUCKET_COLS, rows };
  }

  // ================================
  // FGPZ Z-score DCA strategies
  // ================================
  if (indicatorKey === "fgpz") {
    const s = Array.isArray(fgpzSeries)
      ? fgpzSeries.filter((p) => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (s.length && pricePoints.length) {
      function simulate(entryZ, exitZ) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value < entryZ)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryZ) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value >= exitZ) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "DCA z<-1, Exit z>=1", entry: -1, exit: 1 },
        { name: "DCA z<-0.5, Exit z>=1", entry: -0.5, exit: 1 },
        { name: "DCA z<-1, Exit z>=1.5", entry: -1, exit: 1.5 },
        { name: "DCA z<-1.5, Exit z>=1", entry: -1.5, exit: 1 },
      ];
      const rows = Scenarios.map((scn) => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return { title: "Indicator Backtesting Statistics — FGPZ Z-score DCA (2y; cheap vs growth curve)", columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // GAG momentum strategies
  // ================================
  if (indicatorKey === "gag") {
    const s = Array.isArray(gagSeries)
      ? gagSeries.filter((p) => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (s.length && pricePoints.length) {
      function simulate(entryThr, exitThr) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value > entryThr)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value > entryThr) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value <= exitThr) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "Enter GAG>0 → Exit GAG≤0", entry: 0, exit: 0 },
        { name: "Enter GAG>5 → Exit GAG≤0", entry: 5, exit: 0 },
        { name: "Enter GAG>10 → Exit GAG≤0", entry: 10, exit: 0 },
        { name: "Enter GAG>0 → Exit GAG≤-5", entry: 0, exit: -5 },
      ];
      const rows = Scenarios.map((scn) => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return { title: "Indicator Backtesting Statistics — GAG (growth underpriced vs multiple)", columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // Combined Classic/Novel Fundamental (equal-weight Z)
  // ================================
  if (isClassicNovelCombo(indicatorKey) || indicatorKey === "cnf_combo") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : [];
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];

    if (s.length && pricePoints.length) {
      function simulate(entryZ, exitZ) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value < entryZ)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryZ) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value >= exitZ) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "DCA z<-1, Exit z>=1", entry: -1, exit: 1 },
        { name: "DCA z<-0.5, Exit z>=1", entry: -0.5, exit: 1 },
        { name: "DCA z<-1, Exit z>=1.5", entry: -1, exit: 1.5 },
        { name: "DCA z<-1.5, Exit z>=1", entry: -1.5, exit: 1 },
      ];
      const rows = Scenarios.map(scn => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return {
        title: `Indicator Backtesting Statistics — Combined Classic/Novel (equal-weight; enter z<-1; exit z>=1)`,
        columns: BASE_SCEN_COLS,
        rows,
      };
    }
  }

  // ================================
  // P/E Z-score DCA strategies
  // ================================
  if (indicatorKey === "pe_ratio") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : peNormalizedSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];

    if (s.length && pricePoints.length) {
      function simulate(entryZ, exitZ) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value < entryZ)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryZ) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value >= exitZ) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "DCA z<-1, Exit z>=1", entry: -1, exit: 1 },
        { name: "DCA z<-0.5, Exit z>=1", entry: -0.5, exit: 1 },
        { name: "DCA z<-1, Exit z>=1.5", entry: -1, exit: 1.5 },
        { name: "DCA z<-1.5, Exit z>=1", entry: -1.5, exit: 1 },
      ];
      const rows = Scenarios.map(scn => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return { title: `Indicator Backtesting Statistics — P/E Z-score DCA (${zYearsLabel} avg; enter z<-1; exit z>=1)`, columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // P/Operating Income Z-score DCA
  // ================================
  if (indicatorKey === "poi_ratio") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : poiNormalizedSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];

    if (s.length && pricePoints.length) {
      function simulate(entryZ, exitZ) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value < entryZ)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryZ) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j, exitT = null;
          while (k < s.length) { if (s[k].value >= exitZ) { exitT = s[k].time; break; } k++; }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }
      const Scenarios = [
        { name: "DCA z<-1, Exit z>=1", entry: -1, exit: 1 },
        { name: "DCA z<-0.5, Exit z>=1", entry: -0.5, exit: 1 },
        { name: "DCA z<-1, Exit z>=1.5", entry: -1, exit: 1.5 },
        { name: "DCA z<-1.5, Exit z>=1", entry: -1.5, exit: 1 },
      ];
      const rows = Scenarios.map(scn => {
        const { trades, durs } = simulate(scn.entry, scn.exit);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return { title: `Indicator Backtesting Statistics — P/Operating Income Z-score DCA (${zYearsLabel} avg; enter z<-1; exit z>=1)`, columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // EV/EBITDA yield (raw) → next quarter buckets
  // ================================
  if (indicatorKey === "ev_ebitda") {
    const ranges = [
      { name: "< 0", test: (v) => v < 0 },
      { name: "0–1", test: (v) => v >= 0 && v < 1 },
      { name: "1–2", test: (v) => v >= 1 && v < 2 },
      { name: "2–4", test: (v) => v >= 2 && v < 4 },
      { name: ">= 4", test: (v) => v >= 4 },
    ];
    const buckets = ranges.map((rg) => ({ rg, rets: [] }));
    evYieldSeries?.forEach((pt) => {
      const nextQ = incomePts.find((q) => q.time > pt.time);
      if (!nextQ) return;
      const b = buckets.find((x) => x.rg.test(pt.value));
      if (!b) return;
      b.rets.push({ ret: forwardReturnBetweenTimes(pt.time, nextQ.time) });
    });
    const rows = buckets.map((b) => summarizeBuckets(b.rg.name, b.rets));
    return {
      title: "Indicator Backtesting Statistics — Forward % return by EV/EBITDA yield bucket (until next quarter)",
      columns: BASE_BUCKET_COLS,
      rows,
    };
  }

  // ================================
  // EV/EBITDA yield z-score DCA (high z = cheap yield vs history; exit when z very low)
  // ================================
  if (indicatorKey === "ev_ebitda_z") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : evYieldSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter((p) => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    const win = zYearsLabel;

    if (s.length && pricePoints.length) {
      function simulateYieldDCA(entryHi, exitLo) {
        const trades = [];
        const durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value > entryHi)) {
            i++;
            continue;
          }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value > entryHi) j++;
          const tStart = s[start].time;
          const tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) {
            i = j;
            continue;
          }
          let k = j;
          let exitT = null;
          while (k < s.length) {
            if (s[k].value < exitLo) {
              exitT = s[k].time;
              break;
            }
            k++;
          }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else {
            i = j + 1;
          }
        }
        return { trades, durs };
      }
      const scenarios = [
        { name: "DCA z>1 and sell z<-1", entryHi: 1, exitLo: -1 },
        { name: "DCA z>0.5 and sell z<-1", entryHi: 0.5, exitLo: -1 },
        { name: "DCA z>1 and sell z<-1.5", entryHi: 1, exitLo: -1.5 },
        { name: "DCA z>1.5 and sell z<-1", entryHi: 1.5, exitLo: -1 },
      ];
      const rows = scenarios.map((scn) => {
        const { trades, durs } = simulateYieldDCA(scn.entryHi, scn.exitLo);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return {
        title: `Indicator Backtesting Statistics — EV/EBITDA yield z DCA (${win} window; enter z>entry, exit z<exit)`,
        columns: BASE_SCEN_COLS,
        rows,
      };
    }
  }

  // ================================
  // Dividend Yield z-score DCA strategies (high z = yield above history)
  // ================================
  if (indicatorKey === "div_yield_z") {
    const sRaw = Array.isArray(zScoreSeries) ? zScoreSeries : divYieldZ2ySeries;
    const s = Array.isArray(sRaw)
      ? sRaw.filter((p) => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    const win = zYearsLabel;

    if (s.length && pricePoints.length) {
      function simulateYieldDCA(entryHi, exitLo) {
        const trades = [];
        const durs = [];
        let i = 0;
        while (i < s.length) {
          if (!(s[i].value > entryHi)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value > entryHi) j++;
          const tStart = s[start].time;
          const tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          let k = j;
          let exitT = null;
          while (k < s.length) {
            if (s[k].value < exitLo) { exitT = s[k].time; break; }
            k++;
          }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else i = j + 1;
        }
        return { trades, durs };
      }

      const scenarios = [
        { name: "DCA z>1 and sell z<-1", entryHi: 1, exitLo: -1 },
        { name: "DCA z>0.5 and sell z<-1", entryHi: 0.5, exitLo: -1 },
        { name: "DCA z>1 and sell z<-1.5", entryHi: 1, exitLo: -1.5 },
        { name: "DCA z>1.5 and sell z<-1", entryHi: 1.5, exitLo: -1 },
      ];
      const rows = scenarios.map((scn) => {
        const { trades, durs } = simulateYieldDCA(scn.entryHi, scn.exitLo);
        return summarizeRets({ labelKey: "Scenario", label: scn.name, rets: trades, durs });
      });
      return {
        title: `Indicator Backtesting Statistics — Dividend Yield z DCA (${win} window; enter z>entry, exit z<exit)`,
        columns: BASE_SCEN_COLS,
        rows,
      };
    }
  }

  // ================================
  // PEG raw >1 vs <1 → next quarter
  // ================================
  if (indicatorKey === "peg_ratio_raw") {
    const hi = [], lo = [];
    pegSeries?.forEach(pt => {
      const nextQ = incomePts.find(q => q.time > pt.time);
      if (!nextQ) return;
      const ret = forwardReturnBetweenTimes(pt.time, nextQ.time);
      (pt.value > 1 ? hi : lo).push({ ret });
    });
    const rows = [ summarizeBuckets("PEG > 1", hi), summarizeBuckets("PEG < 1", lo) ];
    return {
      title: "Indicator Backtesting Statistics — Forward % return by PEG bucket (until next quarter)",
      columns: BASE_BUCKET_COLS,
      rows,
    };
  }

  // ================================
  // TAMO DCA
  // ================================
  if (indicatorKey === "tamo") {
    const s = Array.isArray(tamoSeries)
      ? tamoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — TAMO DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    
    function buildTrades(entryThreshold, exitThreshold) {
      const trades = [], durs = [];
      let i = 0;
      while (i < s.length) {
        // Entry: TAMO > entryThreshold
        if (!(s[i]?.value > entryThreshold)) { i++; continue; }
        let j = i, sumEntry = 0, cntEntry = 0;
        const segStartT = s[i].time;
        // DCA: continue buying while TAMO > entryThreshold
        while (j < s.length && s[j].value > entryThreshold) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
        if (!avgEntry) { i = j + 1; continue; }
        
        // Exit: TAMO <= exitThreshold
        let k = j, tExit = null, pExit = null;
        while (k < s.length) {
          if (s[k].value <= exitThreshold) {
            tExit = s[k].time;
            pExit = pxAfter(tExit);
            break;
          }
          k++;
        }
        if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
          trades.push((pExit / avgEntry - 1) * 100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else {
          i = j + 1;
        }
      }
      return { trades, durs };
    }
    
    const scenarios = [
      { name: `DCA TAMO > 10 → Exit TAMO <= -10`, entry: 10, exit: -10 },
      { name: `DCA TAMO > 20 → Exit TAMO <= -20`, entry: 20, exit: -20 },
      { name: `DCA TAMO > 30 → Exit TAMO <= -30`, entry: 30, exit: -30 },
    ];
    
    const rows = scenarios.map(sc => {
      const { trades, durs } = buildTrades(sc.entry, sc.exit);
      return summarizeRets({ labelKey: "Scenario", label: sc.name, rets: trades, durs });
    });
    
    return {
      title: "Indicator Backtesting Statistics — TAMO DCA strategies",
      columns: BASE_SCEN_COLS,
      rows,
    };
  }

  // ================================
  // NIMO DCA
  // ================================
  if (indicatorKey === "nimo") {
    const s = Array.isArray(nimoSeries)
      ? nimoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — NIMO DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    
    function buildTrades(entryThreshold, exitThreshold) {
      const trades = [], durs = [];
      let i = 0;
      while (i < s.length) {
        // Entry: NIMO > entryThreshold
        if (!(s[i]?.value > entryThreshold)) { i++; continue; }
        let j = i, sumEntry = 0, cntEntry = 0;
        const segStartT = s[i].time;
        // DCA: continue buying while NIMO > entryThreshold
        while (j < s.length && s[j].value > entryThreshold) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
        if (!avgEntry) { i = j + 1; continue; }
        
        // Exit: NIMO <= exitThreshold
        let k = j, tExit = null, pExit = null;
        while (k < s.length) {
          if (s[k].value <= exitThreshold) {
            tExit = s[k].time;
            pExit = pxAfter(tExit);
            break;
          }
          k++;
        }
        if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
          trades.push((pExit / avgEntry - 1) * 100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else {
          i = j + 1;
        }
      }
      return { trades, durs };
    }
    
    const scenarios = [
      { name: `DCA NIMO > 10 → Exit NIMO <= -10`, entry: 10, exit: -10 },
      { name: `DCA NIMO > 20 → Exit NIMO <= -20`, entry: 20, exit: -20 },
      { name: `DCA NIMO > 30 → Exit NIMO <= -30`, entry: 30, exit: -30 },
    ];
    
    const rows = scenarios.map(sc => {
      const { trades, durs } = buildTrades(sc.entry, sc.exit);
      return summarizeRets({ labelKey: "Scenario", label: sc.name, rets: trades, durs });
    });
    
    return {
      title: "Indicator Backtesting Statistics — NIMO DCA strategies",
      columns: BASE_SCEN_COLS,
      rows,
    };
  }

  // ================================
  // RMO DCA
  // ================================
  if (indicatorKey === "rmo") {
    const s = Array.isArray(rmoSeries)
      ? rmoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — RMO DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    
    function buildTrades(entryThreshold, exitThreshold) {
      const trades = [], durs = [];
      let i = 0;
      while (i < s.length) {
        // Entry: RMO > entryThreshold
        if (!(s[i]?.value > entryThreshold)) { i++; continue; }
        let j = i, sumEntry = 0, cntEntry = 0;
        const segStartT = s[i].time;
        // DCA: continue buying while RMO > entryThreshold
        while (j < s.length && s[j].value > entryThreshold) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
        if (!avgEntry) { i = j + 1; continue; }
        
        // Exit: RMO <= exitThreshold
        let k = j, tExit = null, pExit = null;
        while (k < s.length) {
          if (s[k].value <= exitThreshold) {
            tExit = s[k].time;
            pExit = pxAfter(tExit);
            break;
          }
          k++;
        }
        if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
          trades.push((pExit / avgEntry - 1) * 100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else {
          i = j + 1;
        }
      }
      return { trades, durs };
    }
    
    const scenarios = [
      { name: `DCA RMO > 10 → Exit RMO <= -10`, entry: 10, exit: -10 },
      { name: `DCA RMO > 20 → Exit RMO <= -20`, entry: 20, exit: -20 },
      { name: `DCA RMO > 30 → Exit RMO <= -30`, entry: 30, exit: -30 },
    ];
    
    const rows = scenarios.map(sc => {
      const { trades, durs } = buildTrades(sc.entry, sc.exit);
      return summarizeRets({ labelKey: "Scenario", label: sc.name, rets: trades, durs });
    });
    
    return {
      title: "Indicator Backtesting Statistics — RMO DCA strategies",
      columns: BASE_SCEN_COLS,
      rows,
    };
  }

  // ================================
  // VDDM DCA variants (using improved percentile normalization: -50 to +50)
  // Improved strategy: enter on deep underwater, exit earlier, add momentum filter
  // ================================
  if (indicatorKey === "vddm") {
    const s = Array.isArray(vddmSeries)
      ? vddmSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — VDDM strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    
    // Calculate price momentum for entry filtering (only enter when recovering)
    const momentum = [];
    for (let i = 0; i < s.length; i++) {
      if (i < 10) { momentum.push(null); continue; }
      const pNow = pxAfter(s[i].time);
      const pPrev = pxAfter(s[i - 10].time);
      if (pNow && pPrev && pPrev > 0) {
        momentum.push((pNow / pPrev - 1) * 100); // 10-day momentum %
      } else {
        momentum.push(null);
      }
    }
    
    function buildTrades(entryCond, exitCond, maxDays = null, requireMomentum = false) {
      const trades = [], durs=[];
      let i=0;
      while (i<s.length) {
        // Entry: must meet VDDM condition AND (optional) positive momentum
        if (!entryCond(s[i]?.value)) { i++; continue; }
        if (requireMomentum && (momentum[i] == null || momentum[i] < 0)) { i++; continue; }
        
        let j=i, sumEntry=0, cntEntry=0, segStartT=s[i].time;
        while (j<s.length && entryCond(s[j]?.value)) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
        if (!avgEntry) { i = j + 1; continue; }
        
        // Exit: meet exit condition OR max days reached
        let k=j, tExit=null, pExit=null;
        while (k<s.length) {
          if (exitCond(s[k]?.value)) {
            tExit = s[k].time;
            pExit = pxAfter(tExit);
            break;
          }
          // Time-based exit if maxDays specified
          if (maxDays && daysBetween(segStartT, s[k].time) >= maxDays) {
            tExit = s[k].time;
            pExit = pxAfter(tExit);
            break;
          }
          k++;
        }
        
        if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    
    // Crossover strategy: buy when VDDM crosses -20 from below, sell when crosses -20 from above
    function buildCrossoverTrades(threshold = -20) {
      const trades = [], durs = [];
      let i = 1; // Start at 1 to compare with previous
      while (i < s.length) {
        const prevVal = s[i - 1]?.value;
        const currVal = s[i]?.value;
        
        // Entry: VDDM crosses threshold from below (was < threshold, now >= threshold)
        if (prevVal != null && currVal != null && 
            prevVal < threshold && currVal >= threshold) {
          const tEntry = s[i].time;
          const pEntry = pxAfter(tEntry);
          if (!(pEntry > 0)) { i++; continue; }
          
          // Exit: VDDM crosses threshold from above (was >= threshold, now < threshold)
          let j = i + 1;
          let tExit = null, pExit = null;
          while (j < s.length) {
            const prevExit = s[j - 1]?.value;
            const currExit = s[j]?.value;
            if (prevExit != null && currExit != null &&
                prevExit >= threshold && currExit < threshold) {
              tExit = s[j].time;
              pExit = pxAfter(tExit);
              break;
            }
            j++;
          }
          
          if (tExit != null && pExit != null && pExit > 0) {
            trades.push((pExit / pEntry - 1) * 100);
            durs.push(daysBetween(tEntry, tExit));
            i = j + 1;
          } else {
            i++;
          }
        } else {
          i++;
        }
      }
      return { trades, durs };
    }
    
    // Crossover strategy: buy when VDDM crosses -20 from below, sell when crosses -20 from above
    const scenarios = [
      { name: "Buy VDDM↑-20 → Sell VDDM↓-20", isCrossover: true, threshold: -20 },
    ];
    
    const rows = scenarios.map(sc => {
      let trades, durs;
      if (sc.isCrossover) {
        // Use crossover strategy
        const result = buildCrossoverTrades(sc.threshold);
        trades = result.trades;
        durs = result.durs;
      } else {
        // Use DCA strategy
        const result = buildTrades(sc.entry, sc.exit, sc.maxDays, sc.momentum);
        trades = result.trades;
        durs = result.durs;
      }
      return summarizeRets({ labelKey:"Scenario", label: sc.name, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — VDDM DCA strategies (improved: earlier exits, momentum filter)", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // CFAO DCA
  // ================================
  if (indicatorKey === "cfao") {
    const s = Array.isArray(cfaoSeries)
      ? cfaoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — CFAO DCA (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades=[], durs=[];
    let i=0;
    while (i<s.length) {
      const isStart = s[i].value > 50 && (i===0 || s[i-1].value <= 50);
      if (!isStart) { i++; continue; }
      const segStartT = s[i].time;
      let j=i, sumEntry=0, cntEntry=0;
      while (j<s.length && s[j].value>50) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
      let k=j, tExit=null, pExit=null;
      while (k<s.length) { if (s[k].value < 50) { tExit=s[k].time; pExit=pxAfter(tExit); break; } k++; }
      if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
        trades.push((pExit/avgEntry - 1)*100);
        durs.push(daysBetween(segStartT, tExit));
        i = k + 1;
      } else i = j + 1;
    }
    return {
      title: "Indicator Backtesting Statistics — CFAO DCA (buy >50, exit <50)",
      columns: BASE_BUCKET_COLS,
      rows: [ summarizeRets({ labelKey:"Bucket", label:"DCA CFAO>50 → Exit CFAO<50", rets: trades, durs }) ],
    };
  }

  // ================================
  // CFAO Delta (ΔCFAO) — Buy on cross above 0, sell on cross below 0
  // ================================
  if (indicatorKey === "cfao_delta") {
    const s = Array.isArray(cfaoDeltaSeries)
      ? cfaoDeltaSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — CFAO Delta (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades = [], durs = [];
    const pxA = t => pxAfter(t);
    let i = 1; // Start at 1 to compare with previous
    while (i < s.length) {
      const prevVal = s[i - 1]?.value;
      const currVal = s[i]?.value;
      // Buy: ΔCFAO crosses above 0 (was <= 0, now > 0)
      if (prevVal != null && currVal != null && prevVal <= 0 && currVal > 0) {
        const tEntry = s[i].time;
        const pEntry = pxA(tEntry);
        if (!(pEntry > 0)) { i++; continue; }
        // Sell: ΔCFAO crosses below 0 (was >= 0, now < 0)
        let j = i + 1;
        let tExit = null, pExit = null;
        while (j < s.length) {
          const prevExit = s[j - 1]?.value;
          const currExit = s[j]?.value;
          if (prevExit != null && currExit != null && prevExit >= 0 && currExit < 0) {
            tExit = s[j].time;
            pExit = pxA(tExit);
            break;
          }
          j++;
        }
        if (tExit != null && pExit != null && pExit > 0) {
          trades.push((pExit / pEntry - 1) * 100);
          durs.push(daysBetween(tEntry, tExit));
          i = j + 1;
        } else {
          i++;
        }
      } else {
        i++;
      }
    }
    const rows = [
      summarizeRets({
        labelKey: "Scenario",
        label: "Buy ΔCFAO↑0 → Sell ΔCFAO↓0",
        rets: trades,
        durs,
      }),
    ];
    return {
      title: "Indicator Backtesting Statistics — CFAO Delta (buy when crosses above 0, sell when crosses below 0)",
      columns: BASE_SCEN_COLS,
      rows,
    };
  }

  // ================================
  // VMZ DCA
  // ================================
  if (indicatorKey === "vmz") {
    const s = Array.isArray(vmzSeries)
      ? vmzSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — VMZ DCA (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades=[], durs=[];
    let i=0;
    while (i<s.length) {
      if (!(s[i]?.value < 0)) { i++; continue; }
      const segStartT = s[i].time;
      let j=i, sumEntry=0, cntEntry=0;
      while (j<s.length && s[j].value < 0) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
      let k=j, tExit=null, pExit=null;
      while (k<s.length) { if (s[k].value >= 0) { tExit=s[k].time; pExit=pxAfter(tExit); break; } k++; }
      if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
        trades.push((pExit/avgEntry - 1)*100);
        durs.push(daysBetween(segStartT, tExit));
        i = k + 1;
      } else i = j + 1;
    }
    return {
      title: "Indicator Backtesting Statistics — VMZ DCA (buy <0, exit ≥0)",
      columns: BASE_BUCKET_COLS,
      rows: [ summarizeRets({ labelKey:"Bucket", label:"DCA VMZ<0 → Exit VMZ≥0", rets: trades, durs }) ],
    };
  }

  // ================================
  // CCE DCA
  // ================================
  if (indicatorKey === "cce") {
    const s = Array.isArray(cceSeries)
      ? cceSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — CCE DCA (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades=[], durs=[];
    let i=0;
    while (i<s.length) {
      const isStart = s[i].value > 50 && (i === 0 || s[i - 1].value <= 50);
      if (!isStart) { i++; continue; }
      const segStartT = s[i].time;
      let j=i, sumEntry=0, cntEntry=0;
      while (j<s.length && s[j].value > 50) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
      let k=j, tExit=null, pExit=null;
      while (k<s.length) { if (s[k].value < 50) { tExit=s[k].time; pExit=pxAfter(tExit); break; } k++; }
      if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
        trades.push((pExit/avgEntry - 1)*100);
        durs.push(daysBetween(segStartT, tExit));
        i = k + 1;
      } else i = j + 1;
    }
    return {
      title: "Indicator Backtesting Statistics — CCE DCA (buy >50, exit <50)",
      columns: BASE_BUCKET_COLS,
      rows: [ summarizeRets({ labelKey:"Bucket", label:"DCA CCE>50 → Exit CCE<50", rets: trades, durs }) ],
    };
  }

  // ================================
  // MRVZ DCA scenarios
  // ================================
  if (indicatorKey === "mrvz") {
    const s = Array.isArray(mrvzSeries)
      ? mrvzSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — MRVZ strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const pxA = t => (priceAtOrAfterEngine(t, pricePoints)?.value ?? null);
    function simulate(entryCond, exitCond) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        if (!entryCond(s[i]?.value)) { i++; continue; }
        let j=i, sumEntry=0, cntEntry=0, segStartT=s[i].time;
        while (j<s.length && entryCond(s[j]?.value)) {
          const p = pxA(s[j].time);
          if (Number.isFinite(p)) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? sumEntry/cntEntry : null;
        let k=j, exitT=null, pExit=null;
        while (k<s.length) { if (exitCond(s[k]?.value)) { exitT=s[k].time; pExit=pxA(exitT); break; } k++; }
        if (avgEntry!=null && Number.isFinite(pExit) && avgEntry>0 && pExit>0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, exitT));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const scenarios = [
      { name: "DCA MRVZ < -2 → Exit MRVZ ≥ 0",   entry:(v)=>v < -2,   exit:(v)=>v >= 0 },
      { name: "DCA MRVZ < -1.5 → Exit MRVZ ≥ 0", entry:(v)=>v < -1.5, exit:(v)=>v >= 0 },
      { name: "DCA MRVZ < -1 → Exit MRVZ ≥ 0",   entry:(v)=>v < -1,   exit:(v)=>v >= 0 },
      { name: "DCA MRVZ < -2 → Exit MRVZ ≥ 1",   entry:(v)=>v < -2,   exit:(v)=>v >= 1 },
      { name: "DCA MRVZ < -1.5 → Exit MRVZ ≥ 1", entry:(v)=>v < -1.5, exit:(v)=>v >= 1 },
      { name: "DCA MRVZ < -1 → Exit MRVZ ≥ 1",   entry:(v)=>v < -1,   exit:(v)=>v >= 1 },
    ];
    const rows = scenarios.map(sc => {
      const { trades, durs } = simulate(sc.entry, sc.exit);
      return summarizeRets({ labelKey:"Scenario", label: sc.name, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — MRVZ DCA strategies", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // OEY percentile DCA
  // ================================
  if (indicatorKey === "oey") {
    const s = Array.isArray(oeySeries)
      ? oeySeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — OEY percentile DCA (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    function buildTrades(entryLvl, exitLvl) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        const isStart = s[i].value >= entryLvl && (i===0 || s[i-1].value < entryLvl);
        if (!isStart) { i++; continue; }
        const segStartT = s[i].time;
        let j=i, sumEntry=0, cntEntry=0;
        while (j<s.length && s[j].value >= entryLvl) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? sumEntry/cntEntry : null;
        let k=j, tExit=null, pExit=null;
        while (k<s.length) { if (s[k].value < exitLvl) { tExit=s[k].time; pExit=pxAfter(tExit); break; } k++; }
        if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const scenarios = [
      { label: "DCA OEY≥70 → Exit OEY<50", entry: 70, exit: 50 },
      { label: "DCA OEY≥60 → Exit OEY<50", entry: 60, exit: 50 },
      { label: "DCA OEY≥50 → Exit OEY<50", entry: 50, exit: 50 },
    ];
    const rows = scenarios.map(sc => {
      const { trades, durs } = buildTrades(sc.entry, sc.exit);
      return summarizeRets({ labelKey:"Scenario", label: sc.label, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — OEY percentile DCA variants", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // VMO strategies
  // ================================
  if (indicatorKey === "vmo") {
    const s = Array.isArray(vmoSeries)
      ? vmoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — VMO strategies",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    function buildTrades(entryCond, exitCond) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        if (!entryCond(s[i]?.value)) { i++; continue; }
        let j=i, sumEntry=0, cntEntry=0, segStartT=s[i].time;
        while (j<s.length && entryCond(s[j]?.value)) {
          const p = pxAfter(s[j].time);
          if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
        let k=j, tExit=null, pExit=null;
        while (k<s.length) { if (exitCond(s[k]?.value)) { tExit=s[k].time; pExit=pxAfter(tExit); break; } k++; }
        if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const rows = [
      (() => { const { trades, durs } = buildTrades(v => v < 0,    v => v > 0);   return summarizeRets({ labelKey:"Scenario", label:"Accumulate while VMO < 0 → Exit at first VMO > 0", rets: trades, durs }); })(),
      (() => { const { trades, durs } = buildTrades(v => v < -0.5, v => v > 0.5); return summarizeRets({ labelKey:"Scenario", label:"Accumulate while VMO < -0.5 → Exit at first VMO > 0.5", rets: trades, durs }); })(),
    ];
    return { title: "Indicator Backtesting Statistics — VMO strategies (DCA & flip exit)", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // Mean reversion windowed bands
  // ================================
  if (indicatorKey === "mean_rev") {
    if (pricePoints.length < stdWindow + 2) {
      return {
        title: "Indicator Backtesting Statistics — Mean reversion strategy (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    const bands = rollingBands(pricePoints, stdWindow, maxSigma);
    const z = pricePoints.map((p, i) => {
      const sd = bands.sd[i]?.value || 0, mu = bands.mean[i]?.value || 0;
      return sd > 0 ? (p.value - mu)/sd : 0;
    });
    const trades=[], durs=[];
    let i=0;
    while (i<z.length) {
      if (!(z[i] <= -1 && z[i] >= -4)) { i++; continue; }
      let j=i, sum=0, cnt=0;
      while (j<z.length && z[j] <= -1 && z[j] >= -4) { sum += pricePoints[j].value; cnt += 1; j++; }
      const avgEntry = cnt ? (sum/cnt) : null;
      let k=j, exitPrice=null;
      while (k<z.length) { if (z[k] >= 1 && z[k] <= 4) { exitPrice = pricePoints[k].value; break; } k++; }
      if (avgEntry != null && exitPrice != null) {
        trades.push((exitPrice/avgEntry - 1) * 100);
        durs.push(daysBetween(pricePoints[i].time, pricePoints[k].time));
      }
      i = (k && k > j) ? k + 1 : j + 1;
    }
    const row = summarizeRets({ labelKey:"Bucket", label:"Accumulate [-4..-1] → Exit [1..4]", rets: trades, durs });
    return {
      title: "Indicator Backtesting Statistics — Mean reversion strategy (DCA in negative bands, sell in positive bands)",
      columns: BASE_BUCKET_COLS,
      rows: [row],
    };
  }

  // ================================
  // P/E deviation DCA strategies
  // ================================
  if (indicatorKey === "pe_dev") {
    const s = Array.isArray(peDevSeries)
      ? peDevSeries.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — P/E deviation DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    function simulate(entryLvl, exitLvl) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        if (!(s[i].value < entryLvl)) { i++; continue; }
        const start=i;
        let j=i; while (j<s.length && s[j].value < entryLvl) j++;
        const segStartT=s[start].time, segEndT=s[j-1].time;
        const avgEntry = avgPriceBetween(segStartT, segEndT);
        if (!(avgEntry>0)) { i=j; continue; }
        let k=j, exitT=null;
        while (k<s.length) { if (s[k].value >= exitLvl) { exitT=s[k].time; break; } k++; }
        if (exitT!=null) {
          const pExit = pxAfter(exitT);
          if (Number.isFinite(pExit) && pExit>0) {
            trades.push((pExit/avgEntry - 1)*100);
            durs.push(daysBetween(segStartT, exitT));
          }
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const Scenarios = [
      { name: "DCA PE<−20 → Exit ≥0",  entry:-20, exit: 0 },
      { name: "DCA PE<−20 → Exit ≥10", entry:-20, exit:10 },
      { name: "DCA PE<−20 → Exit ≥20", entry:-20, exit:20 },
      { name: "DCA PE<−30 → Exit ≥0",  entry:-30, exit: 0 },
      { name: "DCA PE<−30 → Exit ≥10", entry:-30, exit:10 },
      { name: "DCA PE<−30 → Exit ≥20", entry:-30, exit:20 },
    ];
    const rows = Scenarios.map(sc => {
      const { trades, durs } = simulate(sc.entry, sc.exit);
      return summarizeRets({ labelKey:"Scenario", label: sc.name, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — P/E deviation DCA strategies", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // SYO DCA: accumulate while SYO > 50; exit first bar with SYO < 50
  // ================================
  if (indicatorKey === "syo") {
    const s = Array.isArray(syoSeries)
      ? syoSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — SYO DCA (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades = [], durs = [];
    let i = 0;
    while (i < s.length) {
      if (!(s[i].value > 50)) { i++; continue; }
      const start = i;
      let j = i;
      while (j < s.length && s[j].value > 50) j++;
      const tStart = s[start].time;
      const tEnd = s[j - 1].time;
      const avgEntry = avgPriceBetween(tStart, tEnd);
      if (!(avgEntry > 0)) { i = j; continue; }
      let k = j;
      let exitT = null;
      while (k < s.length) {
        if (s[k].value < 50) { exitT = s[k].time; break; }
        k++;
      }
      if (exitT != null) {
        const pExit = pxAfter(exitT);
        if (Number.isFinite(pExit) && pExit > 0) {
          trades.push((pExit / avgEntry - 1) * 100);
          durs.push(daysBetween(tStart, exitT));
        }
        i = k + 1;
      } else {
        i = j + 1;
      }
    }
    return {
      title: "Indicator Backtesting Statistics — SYO DCA (accumulate while SYO > 50; exit when SYO < 50)",
      columns: BASE_SCEN_COLS,
      rows: [ summarizeRets({ labelKey: "Scenario", label: "DCA SYO>50 → Exit SYO<50", rets: trades, durs }) ],
    };
  }

  // ================================
  // Realized Price strategies (VWAC cost basis)
  // ================================
  if (indicatorKey === "realized_price_252" || indicatorKey === "realized_price_504" || indicatorKey === "realized_price_5y") {
    const s = Array.isArray(realizedPriceSeries)
      ? realizedPriceSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: `Indicator Backtesting Statistics — ${INDICATOR_OPTIONS?.[indicatorKey] || "Realized Price"} (insufficient data)`,
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    const trades = [], durs = [];
    let i = 0;
    while (i < s.length) {
      const px = pxAfter(s[i].time);
      if (!(px > 0) || px >= s[i].value) { i++; continue; }
      const startT = s[i].time;
      let j = i;
      while (j < s.length) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p > 0 && p >= s[j].value) break;
        j++;
      }
      const segEndT = j > i ? s[j - 1].time : s[i].time;
      const avgEntry = avgPriceBetween(startT, segEndT);
      if (avgEntry > 0) {
        const exitT = j < s.length ? s[j].time : null;
        if (exitT != null) {
          const pExit = pxAfter(exitT);
          if (Number.isFinite(pExit) && pExit > 0) {
            trades.push((pExit / avgEntry - 1) * 100);
            durs.push(daysBetween(startT, exitT));
          }
        }
      }
      i = j < s.length ? j + 1 : s.length;
    }
    const label = INDICATOR_OPTIONS?.[indicatorKey] || indicatorKey;
    return {
      title: `Indicator Backtesting Statistics — ${label} (DCA while price < realized; exit when price ≥ realized)`,
      columns: BASE_SCEN_COLS,
      rows: [summarizeRets({ labelKey: "Scenario", label: "DCA below realized → exit at/above realized", rets: trades, durs })],
    };
  }

  // ================================
  // NUPL strategies
  // ================================
  if (indicatorKey === "nupl") {
    const s = Array.isArray(nuplSeries) ? nuplSeries.filter(p => Number.isFinite(p?.value)) : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — NUPL (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    function buildTrades(exitLvl) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        if (!(s[i].value < 0)) { i++; continue; }
        const start=i;
        let j=i; while (j<s.length && s[j].value < 0) j++;
        const segStartT = s[start].time, segEndT = s[j-1].time;
        const avgEntry = avgPriceBetween(segStartT, segEndT);
        if (!(avgEntry > 0)) { i = j; continue; }
        let k=j, exitT=null;
        while (k<s.length) { if (s[k].value >= exitLvl) { exitT=s[k].time; break; } k++; }
        if (exitT != null) {
          const pExit = pxAfter(exitT);
          if (Number.isFinite(pExit) && pExit>0) {
            trades.push((pExit/avgEntry - 1)*100);
            durs.push(daysBetween(segStartT, exitT));
          }
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const exits = [0, 0.25, 0.5];
    const rows = exits.map(L => {
      const { trades, durs } = buildTrades(L);
      return summarizeRets({ labelKey:"Scenario", label:`DCA NUPL < 0 → Exit NUPL ≥ ${L}`, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — NUPL scenarios (DCA while NUPL < 0; exit on threshold)", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // ED strategies (percentile + flip) - DEPRECATED
  // ================================
  if (indicatorKey === "ed") {
    const sRaw = Array.isArray(edSeries)
      ? edSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!sRaw.length) {
      return {
        title: "Indicator Backtesting Statistics — ED strategies",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    function rollingPercentile(series, lookback = 20) {
      const vals = series.map(p => p.value);
      const out = [];
      for (let i = 0; i < series.length; i++) {
        const start = Math.max(0, i - lookback + 1);
        const win = vals.slice(start, i + 1).slice().sort((a,b)=>a-b);
        const v = vals[i], n = win.length;
        let pr = 50;
        if (n === 1) pr = v < win[0] ? 0 : 100;
        else if (n > 1) {
          if (v <= win[0]) pr = 0;
          else if (v >= win[n-1]) pr = 100;
          else {
            let lo=0, hi=n-1;
            while (lo<=hi) { const m=(lo+hi)>>1; (win[m] < v) ? (lo=m+1) : (hi=m-1); }
            pr = 100 * (lo / (n - 1));
          }
        }
        out.push({ time: series[i].time, value: pr });
      }
      return out;
    }
    const sPct = rollingPercentile(sRaw, 20);

    function buildTrades(series, entryCond, exitCond) {
      const trades=[], durs=[];
      let i=0;
      while (i<series.length) {
        if (!entryCond(series[i]?.value, i, series)) { i++; continue; }
        const segStartT = series[i].time;
        let j=i, sumEntry=0, cntEntry=0;
        while (j<series.length && entryCond(series[j]?.value, j, series)) {
          const p = pxAfter(series[j].time);
          if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
        let k=j, tExit=null, pExit=null;
        while (k<series.length) { if (exitCond(series[k]?.value, k, series)) { tExit=series[k].time; pExit=pxAfter(tExit); break; } k++; }
        if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const rows = [
      (()=>{ const { trades, durs } = buildTrades(sPct, v=>v>=65, v=>v<45); return summarizeRets({ labelKey:"Scenario", label:"DCA while ED percentile ≥ 65 → Exit when < 45", rets: trades, durs }); })(),
      (()=>{ const { trades, durs } = buildTrades(sRaw, v=>v<0,    v=>v>=0 ); return summarizeRets({ labelKey:"Scenario", label:"DCA while ED < 0 → Exit first ED ≥ 0", rets: trades, durs }); })(),
    ];
    return { title: "Indicator Backtesting Statistics — Estimate Drift (ED) strategies", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // Composite Risk scenarios
  // ================================
  if (indicatorKey === "risk") {
    const s = Array.isArray(riskSeries) ? riskSeries.filter(p => isFinite(p?.value)) : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — Composite Risk (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    
    // DCA strategy: buy while below entry threshold, exit when reaches exit threshold
    function buildTrades(entryLvl, exitLvl) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        if (!(s[i].value < entryLvl)) { i++; continue; }
        const start=i;
        let j=i; while (j<s.length && s[j].value < entryLvl) j++;
        const segStartT = s[start].time, segEndT = s[j-1].time;
        const avgEntry = avgPriceBetween(segStartT, segEndT);
        if (!(avgEntry>0)) { i=j; continue; }
        let k=j, exitT=null;
        while (k<s.length) { if (s[k].value >= exitLvl) { exitT=s[k].time; break; } k++; }
        if (exitT!=null) {
          const pExit = pxAfter(exitT);
          if (isFinite(pExit) && pExit>0) {
            trades.push((pExit/avgEntry - 1)*100);
            durs.push(daysBetween(segStartT, exitT));
          }
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    
    // Specific DCA strategies as requested
    const strategies = [
      { entry: 50, exit: 60, label: "DCA Risk<50 → Exit≥60" },
      { entry: 50, exit: 70, label: "DCA Risk<50 → Exit≥70" },
      { entry: 40, exit: 60, label: "DCA Risk<40 → Exit≥60" },
      { entry: 40, exit: 70, label: "DCA Risk<40 → Exit≥70" },
    ];
    
    const rows = [];
    for (const strat of strategies) {
      const { trades, durs } = buildTrades(strat.entry, strat.exit);
      rows.push(summarizeRets({ 
        labelKey:"Scenario", 
        label: strat.label, 
        rets: trades, 
        durs 
      }));
    }
    
    return { 
      title: "Indicator Backtesting Statistics — Composite Risk scenarios (Improved: multiple entry/exit strategies)", 
      columns: BASE_SCEN_COLS, 
      rows 
    };
  }

  // ================================
  // SRM DCA
  // ================================
  if (indicatorKey === "srm") {
    const s = Array.isArray(srmSeries)
      ? srmSeries.filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — SRM (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    const pxA = t => (priceAtOrAfterEngine(t, pricePoints)?.value ?? null);
    const maxSRM = Math.max(...s.map(p => p.value));
    const scale01 = maxSRM <= 1.5;
    const L = x => scale01 ? x / 100 : x;

    function buildTrades(entry, exit) {
      const trades=[], durs=[];
      let i=0;
      while (i<s.length) {
        const isStart = s[i].value > L(entry) && (i===0 || s[i-1].value <= L(entry));
        if (!isStart) { i++; continue; }
        const segStartT = s[i].time;
        let j=i, sumEntry=0, cntEntry=0;
        while (j<s.length && s[j].value > L(entry)) {
          const p = pxA(s[j].time);
          if (Number.isFinite(p) && p>0) { sumEntry+=p; cntEntry++; }
          j++;
        }
        const avgEntry = cntEntry ? (sumEntry/cntEntry) : null;
        let k=j, tExit=null, pExit=null;
        while (k<s.length) { if (s[k].value <= L(exit)) { tExit=s[k].time; pExit=pxA(tExit); break; } k++; }
        if (avgEntry!=null && Number.isFinite(pExit) && pExit>0) {
          trades.push((pExit/avgEntry - 1)*100);
          durs.push(daysBetween(segStartT, tExit));
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }

    const scenarios = [
      { label: "DCA SRM>70 → Exit SRM≤50", entry: 70, exit: 50 },
      { label: "DCA SRM>70 → Exit SRM≤40", entry: 70, exit: 40 },
      { label: "DCA SRM>70 → Exit SRM≤30", entry: 70, exit: 30 },
    ];
    const rows = scenarios.map(sc => {
      const { trades, durs } = buildTrades(sc.entry, sc.exit);
      return summarizeRets({ labelKey:"Scenario", label: sc.label, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — SRM DCA (enter >70; exit at 50/40/30)", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // P/S Ratio Z-score DCA strategies
  // ================================
  if (indicatorKey === "ps_ratio") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : psNormalizedSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    
    if (s.length && pricePoints.length) {
      function buildTrades(entryThreshold, exitThreshold) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          // Entry: z-score < entryThreshold (e.g., < 0 for below average)
          if (!(s[i].value < entryThreshold)) { i++; continue; }
          const start = i;
          let j = i;
          // DCA: continue buying while z-score < entryThreshold
          while (j < s.length && s[j].value < entryThreshold) j++;
          const tStart = s[start].time, tEnd = s[j-1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          
          // Exit: z-score > exitThreshold (e.g., > 1 for more than 1 std dev above average)
          let k = j, exitT = null;
          while (k < s.length) {
            if (s[k].value > exitThreshold) {
              exitT = s[k].time;
              break;
            }
            k++;
          }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else {
            i = j + 1;
          }
        }
        return { trades, durs };
      }
      
      const scenarios = [
        { label: "Enter <0, Exit >1", entry: 0, exit: 1 },
        { label: "Enter <-0.5, Exit >1", entry: -0.5, exit: 1 },
        { label: "Enter <0, Exit >1.5", entry: 0, exit: 1.5 },
        { label: "Enter <-1, Exit >1", entry: -1, exit: 1 },
      ];
      
      const rows = scenarios.map(sc => {
        const { trades, durs } = buildTrades(sc.entry, sc.exit);
        return summarizeRets({ labelKey: "Scenario", label: sc.label, rets: trades, durs });
      });
      return { title: `Indicator Backtesting Statistics — P/S Ratio Z-score DCA (${zYearsLabel} avg; enter z<-1; exit z≥1)`, columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // P/CF Z-score DCA (z<-1 undervalued, z≥1 overvalued; same as P/B and P/S)
  // ================================
  if (indicatorKey === "cf_price") {
    const sSrc = Array.isArray(zScoreSeries) ? zScoreSeries : cfPriceNormalizedSeries;
    const s = Array.isArray(sSrc)
      ? sSrc.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];

    if (s.length && pricePoints.length) {
      function buildTrades(entryThreshold, exitThreshold) {
        const trades = [], durs = [];
        let i = 0;
        while (i < s.length) {
          // Accumulate: z-score < entryThreshold (e.g. z < -1 = undervalued)
          if (!(s[i].value < entryThreshold)) { i++; continue; }
          const start = i;
          let j = i;
          while (j < s.length && s[j].value < entryThreshold) j++;
          const tStart = s[start].time, tEnd = s[j - 1].time;
          const avgEntry = avgPriceBetween(tStart, tEnd);
          if (!(avgEntry > 0)) { i = j; continue; }
          // Sell: z-score >= exitThreshold (e.g. z ≥ 1 = overvalued)
          let k = j, exitT = null;
          while (k < s.length) {
            if (s[k].value >= exitThreshold) {
              exitT = s[k].time;
              break;
            }
            k++;
          }
          if (exitT != null) {
            const pExit = pxAfter(exitT);
            if (Number.isFinite(pExit) && pExit > 0) {
              trades.push((pExit / avgEntry - 1) * 100);
              durs.push(daysBetween(tStart, exitT));
            }
            i = k + 1;
          } else {
            i = j + 1;
          }
        }
        return { trades, durs };
      }

      const scenarios = [
        { label: "DCA z<-1, Sell z≥1", entry: -1, exit: 1 },
        { label: "DCA z<-0.5, Sell z≥1", entry: -0.5, exit: 1 },
        { label: "DCA z<-1, Sell z≥1.5", entry: -1, exit: 1.5 },
        { label: "DCA z<0, Sell z≥1", entry: 0, exit: 1 },
      ];

      const rows = scenarios.map(sc => {
        const { trades, durs } = buildTrades(sc.entry, sc.exit);
        return summarizeRets({ labelKey: "Scenario", label: sc.label, rets: trades, durs });
      });
      return { title: `Indicator Backtesting Statistics — P/CF Z-score DCA (${zYearsLabel} avg; enter z<-1; exit z≥1)`, columns: BASE_SCEN_COLS, rows };
    }
  }

  // ================================
  // P/E Ratio Comparison (Historical/Forward) DCA strategies
  // ================================
  if (indicatorKey === "pe_peratio") {
    const s = Array.isArray(pePERatioSeries)
      ? pePERatioSeries.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — P/E Ratio Comparison DCA strategies (insufficient data)",
        columns: BASE_SCEN_COLS,
        rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
      };
    }
    function simulate(entryThreshold, exitThreshold) {
      const trades = [], durs = [];
      let i = 0;
      while (i < s.length) {
        // Enter when value > entryThreshold (buying opportunity)
        if (!(s[i].value > entryThreshold)) { i++; continue; }
        const start = i;
        let j = i;
        // Continue accumulating while still above entry threshold (DCA)
        while (j < s.length && s[j].value > entryThreshold) j++;
        const t0 = s[start].time, t1 = s[j-1].time;
        const avgEntry = avgPriceBetween(t0, t1);
        if (!(avgEntry > 0)) { i = j; continue; }
        // Exit when value < exitThreshold (overvalued signal)
        let k = j, exitT = null;
        while (k < s.length) { 
          if (s[k].value < exitThreshold) { 
            exitT = s[k].time; 
            break; 
          } 
          k++; 
        }
        if (exitT != null) {
          const pExit = pxAfter(exitT);
          if (Number.isFinite(pExit) && pExit > 0) {
            trades.push((pExit/avgEntry - 1) * 100);
            durs.push(daysBetween(t0, exitT));
          }
          i = k + 1;
        } else i = j + 1;
      }
      return { trades, durs };
    }
    const Scenarios = [
      { name:"DCA P/E Ratio>1.1 → Exit <0.9", entry:1.1, exit:0.9 },
    ];
    const rows = Scenarios.map(scn => {
      const { trades, durs } = simulate(scn.entry, scn.exit);
      return summarizeRets({ labelKey:"Scenario", label: scn.name, rets: trades, durs });
    });
    return { title: "Indicator Backtesting Statistics — P/E Ratio Comparison DCA strategies", columns: BASE_SCEN_COLS, rows };
  }

  // ================================
  // Revenue Valuation (rev_val) DCA: DCA when <=0.9 (undervalued), sell when >1.1 (overvalued)
  // ================================
  if (indicatorKey === "rev_val") {
    const s = Array.isArray(revValSeries)
      ? revValSeries.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — Revenue Valuation DCA (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades = [], durs = [];
    let i = 0;
    while (i < s.length) {
      if (!(s[i].value <= 0.9)) { i++; continue; }
      const segStartT = s[i].time;
      let j = i, sumEntry = 0, cntEntry = 0;
      while (j < s.length && s[j].value <= 0.9) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
      let k = j, tExit = null, pExit = null;
      while (k < s.length) { if (s[k].value > 1.1) { tExit = s[k].time; pExit = pxAfter(tExit); break; } k++; }
      if (avgEntry != null && Number.isFinite(pExit) && pExit > 0) {
        trades.push((pExit / avgEntry - 1) * 100);
        durs.push(daysBetween(segStartT, tExit));
        i = k + 1;
      } else i = j + 1;
    }
    return {
      title: "Indicator Backtesting Statistics — Revenue Valuation DCA (DCA ≤0.9 → Sell >1.1)",
      columns: BASE_BUCKET_COLS,
      rows: [ summarizeRets({ labelKey:"Bucket", label:"DCA Rev Val ≤0.9 → Exit >1.1", rets: trades, durs }) ],
    };
  }

  // ================================
  // EPS Valuation (eps_val) DCA: same as rev_val, DCA when ≤0.9, sell when >1.1
  // ================================
  if (indicatorKey === "eps_val") {
    const s = Array.isArray(epsValSeries)
      ? epsValSeries.filter(p => Number.isFinite(p?.value) && Number.isFinite(p?.time))
      : [];
    if (!s.length) {
      return {
        title: "Indicator Backtesting Statistics — EPS Valuation DCA (insufficient data)",
        columns: BASE_BUCKET_COLS,
        rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
                 "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}],
      };
    }
    const trades = [], durs = [];
    let i = 0;
    while (i < s.length) {
      if (!(s[i].value <= 0.9)) { i++; continue; }
      const segStartT = s[i].time;
      let j = i, sumEntry = 0, cntEntry = 0;
      while (j < s.length && s[j].value <= 0.9) {
        const p = pxAfter(s[j].time);
        if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
      let k = j, tExit = null, pExit = null;
      while (k < s.length) { if (s[k].value > 1.1) { tExit = s[k].time; pExit = pxAfter(tExit); break; } k++; }
      if (avgEntry != null && Number.isFinite(pExit) && pExit > 0) {
        trades.push((pExit / avgEntry - 1) * 100);
        durs.push(daysBetween(segStartT, tExit));
        i = k + 1;
      } else i = j + 1;
    }
    return {
      title: "Indicator Backtesting Statistics — EPS Valuation DCA (DCA ≤0.9 → Sell >1.1)",
      columns: BASE_BUCKET_COLS,
      rows: [ summarizeRets({ labelKey:"Bucket", label:"DCA EPS Val ≤0.9 → Exit >1.1", rets: trades, durs }) ],
    };
  }

  // fallback
  return {
    title: "Indicator Backtesting Statistics",
    columns: BASE_BUCKET_COLS,
    rows: [{ Bucket:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
             "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
  };
}

// Custom strategy backtesting function
export function computeCustomStrategy({
  indicator,
  indicatorSeries,
  pricePoints,
  strategyType, // 'dca' | 'crossover' | 'single'
  entryThreshold,
  exitThreshold,
  entryOperator, // '<' | '>' | '<=' | '>=' (for DCA/single)
  exitOperator,  // '<' | '>' | '<=' | '>=' (for DCA/single)
  entryCrossoverDir, // 'up' | 'down' (for crossover)
  exitCrossoverDir,   // 'up' | 'down' (for crossover)
  maxDays,       // optional max holding period (only when exitMode === 'indicator')
  exitMode = 'indicator', // 'indicator' | 'holding'
  holdingDays,   // required when exitMode === 'holding': exit at first price bar on/after entry + N calendar days
}) {
  const DIST_COLS = ["<-15%","-15–-5%","-5–0%","0–5%","5–15%",">15%"];
  const BASE_SCEN_COLS = ["Scenario","n","Avg %","Geo Avg %","Std %","Avg Days", "Win-to-Loss Ratio", "Sharpe Ratio", ...DIST_COLS];

  if (!pricePoints?.length || !indicatorSeries?.length) {
    return {
      title: "Custom Strategy Backtest (insufficient data)",
      columns: BASE_SCEN_COLS,
      rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
               "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
    };
  }

  const mean = a => a.length ? a.reduce((s,x)=>s+x,0)/a.length : 0;
  const std  = a => a.length<2 ? 0 : Math.sqrt(a.reduce((s,x)=>s+(x-mean(a))**2,0)/(a.length-1));
  const pct  = (n,d) => d ? (100*n/d) : null;
  // Geometric mean for returns (compound return): converts % to multipliers, multiplies, takes nth root, converts back
  const geometricMean = (returns) => {
    if (!returns.length) return null;
    const valid = returns.filter(r => Number.isFinite(r) && r > -100); // Filter out invalid or >-100% returns
    if (!valid.length) return null;
    const product = valid.reduce((prod, r) => {
      const multiplier = 1 + (r / 100); // Convert % to multiplier
      return prod * multiplier;
    }, 1);
    const geoMean = (Math.pow(product, 1 / valid.length) - 1) * 100; // nth root, convert back to %
    return Number.isFinite(geoMean) ? geoMean : null;
  };
  const barAfter = (t) => priceAtOrAfterEngine(t, pricePoints);
  const pxAfter  = (t) => (barAfter(t)?.value ?? null);
  const daysBetween = (t0, t1) => Math.max(0, Math.round((t1 - t0) / (24 * 3600)));
  const SEC_PER_DAY = 24 * 3600;
  const exitByHolding =
    exitMode === 'holding' && Number.isFinite(holdingDays) && holdingDays >= 1;
  const holdingSec = exitByHolding ? holdingDays * SEC_PER_DAY : null;

  function avgPriceBetween(t0, t1) {
    let sum=0, n=0;
    for (const p of pricePoints) {
      if (p.time < t0) continue;
      if (p.time > t1) break;
      if (Number.isFinite(p.value)) { sum+=p.value; n++; }
    }
    if (n) return sum/n;
    const p0 = pxAfter(t0); return Number.isFinite(p0) ? p0 : null;
  }

  function summarizeRets({ labelKey, label, rets, durs }) {
    const R = rets.filter(Number.isFinite);
    const n = R.length;
    if (!n) {
      const row = { [labelKey]: label, n: 0, "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—" };
      for (const c of DIST_COLS) row[c] = "—";
      return row;
    }
    const m = mean(R), s = std(R);
    const geoM = geometricMean(R);
    const avgDays = (durs && durs.length) ? Math.round(mean(durs.filter(Number.isFinite))) : null;
    const wins = R.filter(x => x > 0).length;
    const losses = R.filter(x => x < 0).length;
    const winLossRatio = losses > 0 ? (wins / losses) : (wins > 0 ? null : 0);
    // Sharpe = (mean - riskFree) / std; risk-free = 0 (per-trade excess return per unit volatility)
    const sharpe = s > 0 ? (m / s) : null;

    const l15   = pct(R.filter(x => x <= -15).length, n);
    const l5_15 = pct(R.filter(x => x <=  -5 && x > -15).length, n);
    const l0_5  = pct(R.filter(x => x <    0 && x >  -5).length, n);
    const g0_5  = pct(R.filter(x => x >=   0 && x <   5).length, n);
    const g5_15 = pct(R.filter(x => x >=   5 && x <  15).length, n);
    const g15p  = pct(R.filter(x => x >=  15).length, n);

    return {
      [labelKey]: label,
      n,
      "Avg %": m.toFixed(2),
      "Geo Avg %": geoM != null ? geoM.toFixed(2) : "—",
      "Std %": s.toFixed(2),
      "Avg Days": avgDays == null ? "—" : String(avgDays),
      "Win-to-Loss Ratio": winLossRatio === null ? "∞" : winLossRatio.toFixed(2),
      "Sharpe Ratio": sharpe != null ? sharpe.toFixed(2) : "—",
      "<-15%":  `${l15.toFixed(2)}%`,
      "-15–-5%":`${l5_15.toFixed(2)}%`,
      "-5–0%":  `${l0_5.toFixed(2)}%`,
      "0–5%":   `${g0_5.toFixed(2)}%`,
      "5–15%":  `${g5_15.toFixed(2)}%`,
      ">15%":   `${g15p.toFixed(2)}%`,
    };
  }

  // Helper to evaluate threshold condition
  function evalCondition(value, threshold, operator) {
    if (!Number.isFinite(value) || !Number.isFinite(threshold)) return false;
    switch (operator) {
      case '<': return value < threshold;
      case '>': return value > threshold;
      case '<=': return value <= threshold;
      case '>=': return value >= threshold;
      default: return false;
    }
  }

  // Filter and sort the indicator series (same as static implementation)
  const s = indicatorSeries
    .filter(p => Number.isFinite(p?.time) && Number.isFinite(p?.value))
    .sort((a, b) => a.time - b.time);
  
  // Remove duplicates by time (keep latest value for each time)
  const seen = new Map();
  const sDeduped = [];
  for (let i = s.length - 1; i >= 0; i--) {
    if (!seen.has(s[i].time)) {
      seen.set(s[i].time, true);
      sDeduped.unshift(s[i]);
    }
  }
  const sClean = sDeduped.length > 0 ? sDeduped : s;

  if (exitMode === 'holding' && !exitByHolding) {
    return {
      title: "Custom Strategy (invalid configuration)",
      columns: BASE_SCEN_COLS,
      rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
               "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
    };
  }

  if (strategyType === 'dca') {
    // DCA: every time the entry condition turns on (leading edge), simulate one trade from that
    // episode to its own exit — episodes can overlap in calendar time; maxDays only affects that trade.
    const trades = [], durs = [];
    const entryOn = (idx) => evalCondition(sClean[idx]?.value, entryThreshold, entryOperator);
    const isDcaStart = (idx) => entryOn(idx) && (idx === 0 || !entryOn(idx - 1));

    for (let i = 0; i < sClean.length; i++) {
      if (!isDcaStart(i)) continue;

      let j = i, sumEntry = 0, cntEntry = 0, segStartT = sClean[i].time;
      while (j < sClean.length && evalCondition(sClean[j]?.value, entryThreshold, entryOperator)) {
        const p = pxAfter(sClean[j].time);
        if (Number.isFinite(p) && p > 0) { sumEntry += p; cntEntry++; }
        j++;
      }
      const avgEntry = cntEntry ? (sumEntry / cntEntry) : null;
      if (!avgEntry) continue;

      if (exitByHolding) {
        const targetT = segStartT + holdingSec;
        const bar = barAfter(targetT);
        if (bar && Number.isFinite(bar.value) && bar.value > 0) {
          trades.push((bar.value / avgEntry - 1) * 100);
          durs.push(daysBetween(segStartT, bar.time));
        }
        continue;
      }

      let k = j, tExit = null, pExit = null;
      while (k < sClean.length) {
        if (evalCondition(sClean[k]?.value, exitThreshold, exitOperator)) {
          tExit = sClean[k].time;
          pExit = pxAfter(tExit);
          break;
        }
        if (maxDays && daysBetween(segStartT, sClean[k].time) >= maxDays) {
          tExit = sClean[k].time;
          pExit = pxAfter(tExit);
          break;
        }
        k++;
      }

      if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
        trades.push((pExit / avgEntry - 1) * 100);
        durs.push(daysBetween(segStartT, tExit));
      }
    }
    
    const label = exitByHolding
      ? `DCA ${indicator} ${entryOperator}${entryThreshold} → Exit after ${holdingDays}d`
      : `DCA ${indicator} ${entryOperator}${entryThreshold} → Exit ${exitOperator}${exitThreshold}`;
    return {
      title: "Custom Strategy Backtesting Statistics",
      columns: BASE_SCEN_COLS,
      rows: [summarizeRets({ labelKey: "Scenario", label, rets: trades, durs })]
    };
  } else if (strategyType === 'crossover') {
    // Crossover: every entry cross is its own trade (parallel / overlapping entries allowed).
    const trades = [], durs = [];
    for (let i = 1; i < sClean.length; i++) {
      const prevVal = sClean[i - 1]?.value;
      const currVal = sClean[i]?.value;

      let entryCross = false;
      if (entryCrossoverDir === 'up') {
        entryCross = prevVal != null && currVal != null && prevVal < entryThreshold && currVal >= entryThreshold;
      } else if (entryCrossoverDir === 'down') {
        entryCross = prevVal != null && currVal != null && prevVal >= entryThreshold && currVal < entryThreshold;
      }

      if (!entryCross) continue;

      const tEntry = sClean[i].time;
      const pEntry = pxAfter(tEntry);
      if (!(pEntry > 0)) continue;

      if (exitByHolding) {
        const targetT = tEntry + holdingSec;
        const bar = barAfter(targetT);
        if (bar && Number.isFinite(bar.value) && bar.value > 0) {
          trades.push((bar.value / pEntry - 1) * 100);
          durs.push(daysBetween(tEntry, bar.time));
        }
        continue;
      }

      let j = i + 1;
      let tExit = null, pExit = null;
      while (j < sClean.length) {
        const prevExit = sClean[j - 1]?.value;
        const currExit = sClean[j]?.value;

        let exitCross = false;
        if (exitCrossoverDir === 'down') {
          exitCross = prevExit != null && currExit != null && prevExit >= exitThreshold && currExit < exitThreshold;
        } else if (exitCrossoverDir === 'up') {
          exitCross = prevExit != null && currExit != null && prevExit < exitThreshold && currExit >= exitThreshold;
        }

        if (exitCross) {
          tExit = sClean[j].time;
          pExit = pxAfter(tExit);
          break;
        }
        if (maxDays && daysBetween(tEntry, sClean[j].time) >= maxDays) {
          tExit = sClean[j].time;
          pExit = pxAfter(tExit);
          break;
        }
        j++;
      }

      if (tExit != null && pExit != null && pExit > 0) {
        trades.push((pExit / pEntry - 1) * 100);
        durs.push(daysBetween(tEntry, tExit));
      }
    }
    
    const entryDir = entryCrossoverDir === 'up' ? '↑' : '↓';
    const exitDir = exitCrossoverDir === 'down' ? '↓' : '↑';
    const label = exitByHolding
      ? `Crossover ${indicator}: Buy on ${entryDir}${entryThreshold} → Exit after ${holdingDays}d`
      : `Crossover ${indicator}: Buy on ${entryDir}${entryThreshold} → Sell on ${exitDir}${exitThreshold}`;
    return {
      title: "Custom Strategy Backtesting Statistics",
      columns: BASE_SCEN_COLS,
      rows: [summarizeRets({ labelKey: "Scenario", label, rets: trades, durs })]
    };
  } else if (strategyType === 'single') {
    // Single: every leading-edge entry (condition turns true) is its own trade to exit.
    const trades = [], durs = [];
    const entryOn = (idx) => evalCondition(sClean[idx]?.value, entryThreshold, entryOperator);
    const isSingleStart = (idx) => entryOn(idx) && (idx === 0 || !entryOn(idx - 1));

    for (let i = 0; i < sClean.length; i++) {
      if (!isSingleStart(i)) continue;

      const tEntry = sClean[i].time;
      const pEntry = pxAfter(tEntry);
      if (!(pEntry > 0)) continue;

      if (exitByHolding) {
        const targetT = tEntry + holdingSec;
        const bar = barAfter(targetT);
        if (bar && Number.isFinite(bar.value) && bar.value > 0) {
          trades.push((bar.value / pEntry - 1) * 100);
          durs.push(daysBetween(tEntry, bar.time));
        }
        continue;
      }

      let j = i + 1, tExit = null, pExit = null;
      while (j < sClean.length) {
        if (evalCondition(sClean[j]?.value, exitThreshold, exitOperator)) {
          tExit = sClean[j].time;
          pExit = pxAfter(tExit);
          break;
        }
        if (maxDays && daysBetween(tEntry, sClean[j].time) >= maxDays) {
          tExit = sClean[j].time;
          pExit = pxAfter(tExit);
          break;
        }
        j++;
      }

      if (tExit != null && Number.isFinite(pExit) && pExit > 0) {
        trades.push((pExit / pEntry - 1) * 100);
        durs.push(daysBetween(tEntry, tExit));
      }
    }
    
    const label = exitByHolding
      ? `Single ${indicator} ${entryOperator}${entryThreshold} → Exit after ${holdingDays}d`
      : `Single ${indicator} ${entryOperator}${entryThreshold} → Exit ${exitOperator}${exitThreshold}`;
    return {
      title: "Custom Strategy Backtesting Statistics",
      columns: BASE_SCEN_COLS,
      rows: [summarizeRets({ labelKey: "Scenario", label, rets: trades, durs })]
    };
  }

  return {
    title: "Custom Strategy (invalid configuration)",
    columns: BASE_SCEN_COLS,
    rows: [{ Scenario:"—", n:"—", "Avg %":"—", "Geo Avg %":"—", "Std %":"—", "Avg Days":"—", "Win-to-Loss Ratio":"—", "Sharpe Ratio":"—",
             "<-15%":"—","-15–-5%":"—","-5–0%":"—","0–5%":"—","5–15%":"—",">15%":"—"}]
  };
}
